import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import App from './App'
import './index.css'
import { AuthProvider } from './lib/auth'
import { isClientError } from './lib/pb'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Expiry dates change rarely; refetching on every window focus is noise.
      refetchOnWindowFocus: false,
      // A deploy restarts PocketBase and the API, so requests in that window
      // fail. Retrying past it turns a visible error into a spinner nobody
      // notices. The default backoff (1s, 2s, 4s...) is uncapped and gave up
      // after ~1s at retry: 1 — the middle of the restart. Four attempts with a
      // 4s ceiling covers roughly 10s, which is longer than a container swap.
      //
      // Only for reads: a mutation is not known to be safe to repeat, so
      // mutations keep the default of no retries.
      //
      // 4xx is excluded because it is an answer, not an outage — retrying it
      // changes nothing and delays the Alert. An expired token (401, which every
      // route reaches by way of errorMessage) would otherwise spin for the full
      // backoff before saying so.
      retry: (failureCount, error) => !isClientError(error) && failureCount < 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
      staleTime: 30_000,
    },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
